import { TellescopeModelFromName, useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { DashboardCard, PaginatedList } from "@tellescope/ui";
import { useTranslation } from "react-i18next";

export interface MedicationsSectionProps {
  contact: TellescopeModelFromName<"endusers">;
}

export const MedicationsSection = ({ contact }: MedicationsSectionProps) => {
  const { t } = useTranslation("contacts");
  const medicationsQuery = useTellescopeInfiniteQuery({
    model: "enduser_medications",
    method: "getSome",
    request: {
      filter: {
        enduserId: contact.id,
      },
    },
    select: (data) =>
      data.pages.flat().map((med) => ({
        id: med.id,
        title: med.title,
        subtitle:
          med.dosage?.value && med.dosage?.unit
            ? `${med.dosage.value} ${med.dosage.unit}`
            : med.dosage?.description || "Dosage not specified",
        subtitleSecondary:
          med.orderStatus === "pending"
            ? "Pending Review"
            : med.orderStatus === "active"
            ? "Active"
            : med.orderStatus === "completed"
            ? "Completed"
            : med.orderStatus === "cancelled"
            ? "Cancelled"
            : "Unknown Status",
        description: med.startedTakingAt
          ? new Date(med.startedTakingAt).toLocaleDateString("en-US", {
              month: "2-digit",
              day: "2-digit",
              year: "numeric",
            })
          : med.createdAt
          ? new Date(med.createdAt).toLocaleDateString("en-US", {
              month: "2-digit",
              day: "2-digit",
              year: "numeric",
            })
          : "Date not specified",
        assignees: med.prescriberName
          ? [
              {
                name: med.prescriberName,
                alt: "Prescriber",
              },
            ]
          : [],
      })),
  });

  if (medicationsQuery.isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-center space-y-4">
          <div className="text-sm text-muted-foreground">{t("Loading medications...")}</div>
        </div>
      </div>
    );
  }

  if (!medicationsQuery.data?.length) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-center space-y-4">
          <div className="text-sm text-muted-foreground">{t("No medications found")}</div>
        </div>
      </div>
    );
  }

  return (
    <PaginatedList
      className="flex flex-col gap-4 overflow-auto p-1"
      onBottomReached={() => medicationsQuery.fetchNextPage()}
    >
      {medicationsQuery.data.map((medication) => (
        <div key={medication.id} className="cursor-pointer">
          <DashboardCard
            statusLabel=""
            title={medication.title}
            subtitle={medication.subtitle}
            subtitleSecondary={medication.subtitleSecondary}
            assignees={medication.assignees}
            description={medication.description}
            dueDate=""
            priority=""
            timestamp={undefined}
            showUnreadDot={false}
            className="mb-2"
          />
        </div>
      ))}
    </PaginatedList>
  );
};
